import { useState, useEffect } from "react";
import { SecurityAdvisoryCard, ApiSecurityData } from "./SecurityAdvisoryCard";
import { EconomicRiskCard } from "./EconomicRiskCard";
import { SDGPolicyCard } from "./SDGPolicyCard";
import { TravelAdvisoryCard, TravelData } from "./TravelAdvisoryCard";
import { HistoricalRiskMapCard } from "./HistoricalRiskMapCard";
import { CountrySelector, Country } from "./CountrySelector";
import { TimeSeries } from "@/helpers/chartData";
import { SDGData } from "./SDGPolicyCard";

import { HomeIcon } from "@/icons/Home";
import { FileIcon } from "@/icons/File";
import { GearIcon } from "@/icons/Gear";

interface DashboardData {
  security: ApiSecurityData;
  economic: TimeSeries[];
  sdg: SDGData | null;
  travel: TravelData | null;
}

// API URL is imported from auto-generated config (dev) or replaced at build time (production)
import { API_URL } from "../config/env";

export function Dashboard() {
  const [selectedCountry, setSelectedCountry] = useState<Country>({name: "Pakistan", region: "South Asia", iso: "PAK", alias: [], coordinates: null} as Country);
  const [isLoading, setIsLoading] = useState(true);
  const [securityData, setSecurityData] = useState<ApiSecurityData>({
    description: "No description available.",
    timeSeries: [],
  });
  const [economicData, setEconomicData] = useState<TimeSeries[]>([]);
  const [sdgPolicy, setSdgPolicy] = useState<SDGData | null>(null);
  const [travelData, setTravelData] = useState<TravelData | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/dashboard?country=${encodeURIComponent(selectedCountry.iso)}`
        );
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}.`);
        }
        const apiData: DashboardData = await response.json();
        setSecurityData(apiData.security);
        setEconomicData(apiData.economic);
        setSdgPolicy(apiData.sdg);
        setTravelData(apiData.travel);
      } catch (error) {
        // Log error but continue with empty chart data
        console.error(
          "Failed to fetch dashboard data from API:",
          error,
          "Switch to fallback data."
        );
        // Fallback to default values
        setSecurityData({
          description: "No description available.",
          timeSeries: [],
        });
        setEconomicData([]);
        setSdgPolicy(null);
        setTravelData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboardData();
  }, [selectedCountry]);

  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* Header */}
      <header className="bg-[#161b22] border-b border-gray-800 px-6 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3 min-[372px]:justify-start justify-center w-full sm:w-auto">
            <img 
              src="/favicon.png" 
              alt="HawkEye Watcher Logo" 
              className="w-8 h-8 sm:w-10 sm:h-10"
            />
            <h1 className="text-2xl font-bold text-white hidden min-[372px]:block">
              Political Risk Advisory
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <CountrySelector
              selectedCountry={selectedCountry}
              onCountryChange={setSelectedCountry}
            />
            <button className="text-gray-400 hover:text-white">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Hidden on mobile and tablets, visible on desktop */}
        <aside className="hidden xl:block w-64 bg-[#161b22] min-h-screen border-r border-gray-800">
          <nav className="p-4 space-y-2">
            <button className="w-full flex items-center gap-3 px-4 py-3 text-white bg-[#1a1f2e] rounded-lg">
              <HomeIcon />
              Dashboard
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white hover:bg-[#1a1f2e] rounded-lg">
              <FileIcon />
              Reports
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white hover:bg-[#1a1f2e] rounded-lg">
              <GearIcon />
              Settings
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 xl:auto-rows-[140px]">
              {/* Security Advisory - Spans 1 column */}
              <div className="xl:col-span-1 xl:row-span-3 h-full">
                <SecurityAdvisoryCard country={selectedCountry.name} data={securityData} isLoading={isLoading} />
              </div>

              {/* Economic Risk */}
              <div className="xl:col-span-1 xl:row-span-2 h-full">
                <EconomicRiskCard data={economicData} isLoading={isLoading} />
              </div>

              {/* SDG Policy */}
              <div className="xl:col-span-1 xl:row-span-2 h-full">
                <SDGPolicyCard data={sdgPolicy} isLoading={isLoading} />
              </div>

              {/* Historical Risk Map - Spans 2 columns on large screens */}
              <div className="xl:col-span-2 xl:row-span-3 h-full min-h-[400px]">
                <HistoricalRiskMapCard country={selectedCountry} />
              </div>

              {/* Travel Advisory */}
              <div className="xl:col-span-1 xl:row-span-2 h-full">
                <TravelAdvisoryCard data={travelData} isLoading={isLoading} />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
